import { useState } from 'react';
import { Mail, FileText, Database, ExternalLink, RotateCcw, GitCompare, Clock, User, Hash, CheckCircle2, Circle, Download, HardDrive, Tag, Settings2, ScrollText, MoreHorizontal, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Separator } from '../ui/separator';
import { format } from 'date-fns';
import { templateClassifications } from '../../data/rulesData';
import type { TemplateDetail as TemplateDetailType, TemplateVersion } from '../../types/template.types';
import type { Rule } from '../../types/rule.types';

interface TemplateDetailProps {
  template: TemplateDetailType;
  rules: Rule[];
  onEdit: () => void;
  onDiff: (v1: TemplateVersion, v2: TemplateVersion) => void;
  onActivate: (version: TemplateVersion) => void;
  onEditRule: (rule: Rule) => void;
  onAddRule: (templateId: string) => void;
  onEditInfo: () => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  renderRulesTab?: React.ReactNode;
  renderRuleTestTab?: React.ReactNode;
  renderTestRenderTab?: React.ReactNode;
  auditLog?: Array<{
    entityId: string;
    action: string;
    field?: string;
    oldValue?: string;
    newValue?: string;
    user: string;
    timestamp: string;
  }>;
}

function TypeIcon({ type }: { type: string }) {
  if (type === 'pdf') return (
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-pdf/10">
      <FileText className="h-4.5 w-4.5 text-pdf" />
    </div>
  );
  if (type === 'ledes') return (
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-ledes/10">
      <Database className="h-4.5 w-4.5 text-ledes" />
    </div>
  );
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-email/10">
      <Mail className="h-4.5 w-4.5 text-email" />
    </div>
  );
}

export default function TemplateDetail({
  template,
  rules,
  onEdit,
  onDiff,
  onActivate,
  onEditRule,
  onAddRule,
  onEditInfo,
  activeTab,
  onTabChange,
  renderRulesTab,
  renderRuleTestTab,
  renderTestRenderTab,
  auditLog,
}: TemplateDetailProps) {
  const [diffFrom, setDiffFrom] = useState<number | null>(null);
  const activeVersion = template.versions.find(v => v.isActive);
  const sortedVersions = [...template.versions].sort((a, b) => b.version - a.version);
  const isBase = template.owner === 'base';
  const activeRuleCount = rules.filter(r => r.status === 'active').length;
  const classificationLabel = templateClassifications.find(c => c.value === template.classification)?.label ?? template.classification;

  return (
    <div className="h-full overflow-y-auto studio-scrollbar p-6 space-y-6">
      {/* Header Card */}
      <div className="bg-studio-surface rounded-lg border border-border p-5 space-y-4" data-tour="template-detail">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <TypeIcon type={template.templateType} />
            <div>
              <h2 className="text-lg font-semibold text-foreground">{template.name}</h2>
              <p className="text-xs font-mono text-muted-foreground">{template.path}</p>
            </div>
          </div>
          <Button size="sm" onClick={onEdit} className="h-8 text-xs shrink-0" data-tour="edit-template-btn">Edit Template</Button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-[11px] font-medium uppercase tracking-wide">
            {template.templateType === 'ledes' ? 'LEDES' : template.templateType}
          </Badge>
          <Badge variant="outline" className="text-[10px] gap-1">
            <Tag className="h-2.5 w-2.5" />
            {classificationLabel}
          </Badge>
          {activeVersion && (
            <div className="flex items-center gap-1.5 text-active">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Active v{activeVersion.version}</span>
            </div>
          )}
          {!isBase && rules.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {activeRuleCount} active rule{activeRuleCount !== 1 ? 's' : ''}
            </span>
          )}
          {template.forkedFrom && (
            <span className="text-xs text-muted-foreground">
              Forked from: <span className="font-mono">{template.forkedFrom.templateId}</span>
            </span>
          )}
          {template.storageConfig?.saveOutput && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <HardDrive className="h-3 w-3 text-active" />
              <span className="font-mono text-[10px]">{template.storageConfig.destination}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={onEditInfo}>
            <Settings2 className="h-3.5 w-3.5 mr-1" />
            Edit Info
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
            if (sortedVersions.length >= 2) onDiff(sortedVersions[1]!, sortedVersions[0]!);
          }} title="Diff latest versions">
            <GitCompare className="h-3.5 w-3.5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => {
                if (sortedVersions.length >= 2) onDiff(sortedVersions[1]!, sortedVersions[0]!);
              }}>
                <GitCompare className="h-3.5 w-3.5 mr-2" />
                Diff latest versions
              </DropdownMenuItem>
              <DropdownMenuItem>
                <ExternalLink className="h-3.5 w-3.5 mr-2" />
                View on GitHub
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {activeVersion && (
          <>
            <Separator />
            <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {format(new Date(activeVersion.timestamp), 'MMM d, yyyy h:mm a')}
              </span>
              <span className="flex items-center gap-1">
                <Hash className="h-3 w-3" />
                <span className="font-mono">{activeVersion.commitSha.slice(0, 7)}</span>
              </span>
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {activeVersion.author}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Tabbed Content */}
      <Tabs value={activeTab || 'overview'} onValueChange={onTabChange} className="space-y-4" data-tour="tabs-area">
        <TabsList className="bg-muted/50 h-9">
          <TabsTrigger value="overview" className="text-xs h-7 px-3">Overview</TabsTrigger>
          <TabsTrigger value="test-render" className="text-xs h-7 px-3">Test Render</TabsTrigger>
          {!isBase && (
            <TabsTrigger value="rules" className="text-xs h-7 px-3">
              Rules
              {rules.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] h-4 px-1.5 min-w-[1rem] justify-center">
                  {rules.length}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {!isBase && (
            <TabsTrigger value="test" className="text-xs h-7 px-3">Rule Test</TabsTrigger>
          )}
          <TabsTrigger value="history" className="text-xs h-7 px-3">History</TabsTrigger>
          <TabsTrigger value="audit" className="text-xs h-7 px-3">
            <ScrollText className="h-3 w-3 mr-1" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="bg-studio-surface rounded-lg border border-border p-4 space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Template Summary</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-muted-foreground">Type</span>
                <p className="font-medium text-foreground">{template.templateType === 'ledes' ? 'LEDES' : template.templateType.toUpperCase()}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Classification</span>
                <p className="font-medium text-foreground">{classificationLabel}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Owner</span>
                <p className="font-medium text-foreground">{template.owner === 'base' ? 'Base Templates' : template.owner}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Versions</span>
                <p className="font-medium text-foreground">{template.versions.length}</p>
              </div>
              {!isBase && (
                <div>
                  <span className="text-muted-foreground">Linked Rules</span>
                  <p className="font-medium text-foreground">{rules.length} ({activeRuleCount} active)</p>
                </div>
              )}
            </div>
          </div>
          {template.content && (
            <div className="bg-studio-surface rounded-lg border border-border p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Active Version Content</h3>
              <pre className="text-xs font-mono text-muted-foreground bg-muted/50 rounded p-3 max-h-48 overflow-y-auto studio-scrollbar whitespace-pre-wrap">
                {template.content.slice(0, 600)}{template.content.length > 600 ? '...' : ''}
              </pre>
            </div>
          )}
        </TabsContent>

        <TabsContent value="test-render">{renderTestRenderTab}</TabsContent>
        <TabsContent value="rules">{renderRulesTab}</TabsContent>
        <TabsContent value="test">{renderRuleTestTab}</TabsContent>

        <TabsContent value="history">
          <div className="bg-studio-surface rounded-lg border border-border" data-tour="version-history">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Version History</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {diffFrom !== null
                    ? `Select a version to compare against v${diffFrom}`
                    : 'Click "Compare" on any version, then select another to diff'}
                </p>
              </div>
              {diffFrom !== null && (
                <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setDiffFrom(null)}>
                  Cancel Compare
                </Button>
              )}
            </div>
            <div className="divide-y divide-border">
              {sortedVersions.map((version, idx) => {
                const prevVersion = sortedVersions[idx + 1];
                const isCompareSource = diffFrom === version.version;
                return (
                  <div
                    key={version.version}
                    role={diffFrom !== null && !isCompareSource ? 'button' : undefined}
                    tabIndex={diffFrom !== null && !isCompareSource ? 0 : undefined}
                    className={`px-5 py-4 transition-colors ${
                      isCompareSource ? 'bg-primary/5 ring-1 ring-inset ring-primary/20' :
                      diffFrom !== null ? 'hover:bg-muted/50 cursor-pointer' : ''
                    } ${version.isActive ? 'bg-active/[0.03]' : ''}`}
                    onClick={() => {
                      if (diffFrom !== null && !isCompareSource) {
                        const fromVer = template.versions.find(v => v.version === diffFrom)!;
                        const older = fromVer.version < version.version ? fromVer : version;
                        const newer = fromVer.version < version.version ? version : fromVer;
                        onDiff(older, newer);
                        setDiffFrom(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && diffFrom !== null && !isCompareSource) {
                        e.preventDefault();
                        const fromVer = template.versions.find(v => v.version === diffFrom)!;
                        const older = fromVer.version < version.version ? fromVer : version;
                        const newer = fromVer.version < version.version ? version : fromVer;
                        onDiff(older, newer);
                        setDiffFrom(null);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center gap-0.5 mt-0.5">
                          {version.isActive ? (
                            <CheckCircle2 className="h-4 w-4 text-active" />
                          ) : (
                            <Circle className="h-4 w-4 text-muted-foreground/40" />
                          )}
                          {idx < sortedVersions.length - 1 && (
                            <div className="w-px h-8 bg-border" />
                          )}
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">v{version.version}</span>
                            {version.isActive && (
                              <Badge className="bg-active/10 text-active border-active/20 text-[10px] h-5">Active</Badge>
                            )}
                            {diffFrom !== null && !isCompareSource && (
                              <span className="text-[10px] text-primary font-medium flex items-center gap-1">
                                <ArrowRight className="h-2.5 w-2.5" />
                                Click to compare
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-mono">{version.commitSha.slice(0, 7)}</span>
                            {' · "'}
                            {version.message}
                            {'"'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            by {version.author} · {format(new Date(version.timestamp), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                      </div>

                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant={isCompareSource ? 'secondary' : 'ghost'}
                          size="sm"
                          className="h-7 text-[11px] text-muted-foreground hover:text-foreground"
                          onClick={() => setDiffFrom(isCompareSource ? null : version.version)}
                        >
                          <GitCompare className="h-3 w-3 mr-1" />
                          {isCompareSource ? 'Comparing...' : 'Compare'}
                        </Button>
                        {prevVersion && diffFrom === null && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-[11px] text-muted-foreground hover:text-foreground"
                            onClick={() => onDiff(prevVersion, version)}
                          >
                            Diff vs v{prevVersion.version}
                          </Button>
                        )}
                        {!version.isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-[11px] gap-1 border-active/30 text-active hover:bg-active/10 hover:text-active"
                            onClick={() => onActivate(version)}
                          >
                            <RotateCcw className="h-3 w-3" />
                            Activate
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="bg-studio-surface rounded-lg border border-border">
            <div className="px-5 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Audit Log</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Track all metadata changes made to this template</p>
            </div>
            {(!auditLog || auditLog.length === 0) ? (
              <div className="px-5 py-10 text-center">
                <ScrollText className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No changes recorded yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {[...auditLog].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((entry, i) => (
                  <div key={i} className="px-5 py-3 flex items-start gap-3">
                    <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                      <User className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-foreground">{entry.user}</span>
                        <span className="text-[11px] text-muted-foreground">{entry.action}</span>
                      </div>
                      {entry.field && (
                        <div className="text-[11px] text-muted-foreground">
                          <span className="font-mono text-foreground/70">{entry.field}</span>
                          {entry.oldValue && (
                            <span>: <span className="line-through text-destructive/70">{entry.oldValue}</span></span>
                          )}
                          {entry.newValue && (
                            <span> → <span className="text-active">{entry.newValue}</span></span>
                          )}
                        </div>
                      )}
                      <p className="text-[10px] text-muted-foreground">
                        {format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
